import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toEmbedUrl } from '../lib/embed';
import { PlayCircle } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const Videos = () => {
    const [videos, setVideos] = useState([]);
    const [active, setActive] = useState(null);

    useSEO({
        title: 'Videos — Dry Ice Blasting & Industrial Cleaning in Action',
        description: 'Watch dry ice blasting, laser cleaning, sandblasting, and pressure washing on real industrial jobs — molds, machinery, food processing equipment, and more.',
    });

    useEffect(() => {
        api.get('/videos').then((r) => setVideos(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="videos-page">
            <section className="border-b border-white/10">
                <div className="container-page pt-32 pb-16">
                    <div className="label-caps text-brand-primary mb-6">/ Videos</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl">
                        See the work
                    </h1>
                    <p className="mt-6 text-white/60 max-w-2xl">
                        Field footage of dry ice, sandblasting, pressure washing and laser cleaning
                        across industrial sites.
                    </p>
                </div>
            </section>

            <section className="container-page py-16">
                {videos.length === 0 ? (
                    <div className="text-white/50 label-caps" data-testid="videos-empty">No videos yet.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
                        {videos.map((v) => (
                            <button
                                key={v.id}
                                onClick={() => setActive(v)}
                                className="group text-left border border-white/10 hover:border-brand-primary transition-colors bg-brand-surface"
                                data-testid={`video-card-${v.id}`}
                            >
                                <div className="aspect-video relative bg-brand-bg overflow-hidden">
                                    {v.thumbnail_url ? (
                                        <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                    ) : (
                                        <div className="absolute inset-0 bg-brand-surface2" />
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <PlayCircle className="h-14 w-14 text-white drop-shadow-lg group-hover:scale-110 transition-transform" strokeWidth={1.25} />
                                    </div>
                                </div>
                                <div className="p-5">
                                    <h3 className="font-display font-black text-lg uppercase tracking-tight text-white group-hover:text-brand-primary transition-colors">{v.title}</h3>
                                    <p className="text-sm text-white/60 mt-2 line-clamp-2">{v.description}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {active && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setActive(null)}
                    data-testid="video-modal"
                >
                    <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-black text-xl text-white">{active.title}</h3>
                            <button onClick={() => setActive(null)} className="label-caps text-white/70 hover:text-white" data-testid="video-modal-close">Close ✕</button>
                        </div>
                        <div className="aspect-video border border-white/10">
                            <iframe
                                src={toEmbedUrl(active.video_url)}
                                className="w-full h-full"
                                title={active.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Videos;
